import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import Cookies from "js-cookie";
import useWindowDimensions from "../../components/formatted/UseWindowsDimensions";
import { useTranslation } from "react-i18next";
import {
  formattedDate,
  formattedTime,
  localeMap,
} from "../formatted/FormattedDateAndTime";
import { EchartsZoomFrame, useEchartsZoomCompensation } from "./echartsZoom";

const AKANTHAS_CYAN = "#00A7A6";
const AKANTHAS_NIGHT = "#0F265C";

const buildSelectedMarkPoint = (point) => {
  if (!point) return null;
  return {
    coord: [point[0], point[1]],
    symbol: "circle",
    symbolSize: 10,
    itemStyle: {
      color: AKANTHAS_NIGHT,
      borderColor: "#FFFFFF",
      borderWidth: 2,
    },
    label: { show: false },
  };
};

export const LineChart = ({
  chartName,
  dataChart,
  dataChart2,
  dataChart3,
  dataCompact,
  unitValueChart,
  maxYAxis,
  messageLabelTooltip,
  stepYAxis,
  dataForCompactOverlay,
  limitFillingLevel,
  container,
  historyCurrent,
  findHistoryId,
  showPackmat,
  showLimitFillingLevel,
  showToolbox,
  showTitle = true,
  chartHeightDivisor = 3.5,
  enableHudInteraction = false,
  selectedReadingTime = null,
}) => {
  const { t, i18n } = useTranslation();
  const { zoomLevel } = useEchartsZoomCompensation();

  const { heightWindow, widthWindow } = useWindowDimensions();
  const debug = process.env.REACT_APP_DEBUG;
  const chartRef = useRef(null);
  const chartDomRef = useRef(null);
  const lockedPointRef = useRef(null);
  const currentFillingLevel = dataChart?.[0]?.[1];
  const chartHeight = heightWindow / chartHeightDivisor;

  const cookieSideNavExpandedValue = Cookies.get("sideNavExpanded");
  const parsedSideNavExpandedValue = cookieSideNavExpandedValue
    ? JSON.parse(cookieSideNavExpandedValue)
    : true;

  useEffect(
    () => {
      if (dataForCompactOverlay?.length > 0 && chartDomRef.current) {
        const chart = echarts.init(chartDomRef.current);
        chartRef.current = chart;

        // Chronological copy — never mutate the incoming array.
        const cycleData = [...(dataChart || [])].reverse();
        const cycleData2 = dataChart2 ? [...dataChart2].reverse() : undefined;
        const cycleData3 = dataChart3 ? [...dataChart3].reverse() : undefined;

        const resolvePointFromTime = (time) => {
          if (time == null) return null;
          return (
            cycleData.find((point) => point?.[0] === time) ||
            cycleData.find(
              (point) =>
                point?.[0] != null &&
                new Date(point[0]).getTime() === new Date(time).getTime(),
            ) ||
            null
          );
        };

        if (enableHudInteraction) {
          lockedPointRef.current =
            resolvePointFromTime(selectedReadingTime) || lockedPointRef.current;
        }

        const generateSeries = () => {
          const selectedMark = enableHudInteraction
            ? buildSelectedMarkPoint(lockedPointRef.current)
            : null;
          const packmatMarks =
            showPackmat && Array.isArray(dataCompact) ? dataCompact : [];
          const markPointData = [
            ...packmatMarks,
            ...(selectedMark ? [selectedMark] : []),
          ];

          const series = [
            {
              name: "Cycle",
              type: "line",
              color: enableHudInteraction
                ? AKANTHAS_CYAN
                : "rgba(75,192,192,1)",
              showSymbol: enableHudInteraction,
              symbol: "circle",
              symbolSize: enableHudInteraction ? 9 : 4,
              itemStyle: enableHudInteraction
                ? {
                    color: AKANTHAS_CYAN,
                    borderColor: "#FFFFFF",
                    borderWidth: 2,
                  }
                : undefined,
              areaStyle: {
                color: enableHudInteraction
                  ? "rgba(0, 167, 166, 0.12)"
                  : "rgba(75,192,192,0.1)",
              },
              emphasis: enableHudInteraction
                ? {
                    focus: "series",
                    scale: false,
                    itemStyle: {
                      color: AKANTHAS_NIGHT,
                      borderColor: "#FFFFFF",
                      borderWidth: 2,
                    },
                  }
                : {
                    focus: "series",
                    itemStyle: {
                      borderWidth: 15,
                    },
                    symbolSize: 15,
                  },
              data: cycleData,
              markPoint: {
                data: markPointData.length > 0 ? markPointData : undefined,
                label: {
                  color: "#FFF",
                },
              },
              markLine: {
                silent: true,
                symbol: ["none", "none"],
                lineStyle: {
                  color: "#ACACAC",
                  width: 1,
                },
                label: {
                  normal: {
                    show: true,
                    offset: [-130, 0],
                    top: "middle",
                    formatter: `${t("Limit filling level")} {a|(${limitFillingLevel}%)}`,
                    rich: {
                      a: {
                        fontWeight: "bold",
                      },
                    },
                    backgroundColor:
                      currentFillingLevel > limitFillingLevel
                        ? "#E5193C"
                        : "#E8E8E8",
                    color: currentFillingLevel > limitFillingLevel && "#FFFFFF",
                    padding: [4, 8],
                    borderRadius: 20,
                    borderColor:
                      currentFillingLevel > limitFillingLevel
                        ? "#E5193C"
                        : "#E8E8E8",
                    borderWidth: 1,
                  },
                },
                data: showLimitFillingLevel &&
                  limitFillingLevel && [
                    {
                      yAxis: limitFillingLevel,
                    },
                  ],
              },
            },
            debug === "true" && {
              name: "Cycle",
              type: "line",
              color: "rgba(244,67,54,1)",
              areaStyle: {
                color: "rgba(244,67,54,0.1)",
              },
              emphasis: {
                focus: "series",
              },
              data: cycleData2,
            },
            debug === "true" && {
              name: "Cycle",
              type: "line",
              color: "rgba(255,217,102,1)",
              areaStyle: {
                color: "rgba(255,217,102,0.1)",
              },
              emphasis: {
                focus: "series",
              },
              data: cycleData3,
            },
          ];

          if (showPackmat && dataCompact) {
            const packmatLineData = dataCompact?.map((item) => {
              return {
                type: "line",
                data: [[item.xAxis, 100]],
                showSymbol: false,
                markLine: {
                  silent: true,
                  symbol: ["none", "none"],
                  label: {
                    normal: {
                      show: false,
                    },
                  },
                  data: [
                    {
                      type: "average",
                      valueIndex: 0,
                      itemStyle: {
                        normal: {
                          color: "#ACACAC",
                        },
                      },
                    },
                  ],
                },
              };
            });
            series.push(...packmatLineData);
          }

          return series;
        };

        const generateOptions = () => {
          return {
            ...(showTitle
              ? {
                  title: {
                    text: messageLabelTooltip,
                    left: "1%",
                    textStyle: {
                      color: "#17a2b8",
                    },
                  },
                }
              : {}),
            tooltip: {
              trigger: "axis",
              axisPointer: enableHudInteraction
                ? {
                    type: "cross",
                    animation: true,
                    animationDurationUpdate: 200,
                    crossStyle: {
                      color: AKANTHAS_CYAN,
                      width: 1.25,
                      type: "dashed",
                      opacity: 0.85,
                    },
                    lineStyle: {
                      color: AKANTHAS_CYAN,
                      width: 1,
                      type: "solid",
                      opacity: 0.35,
                    },
                    label: {
                      show: true,
                      backgroundColor: AKANTHAS_NIGHT,
                      color: "#FFFFFF",
                      borderRadius: 6,
                      padding: [5, 8],
                      fontSize: 12,
                      fontWeight: 600,
                      formatter: (params) => {
                        if (params.axisDimension === "y") {
                          const value = Number(params.value);
                          return Number.isFinite(value)
                            ? `${Math.round(value)}${unitValueChart || "%"}`
                            : "";
                        }
                        const date = new Date(params.value);
                        if (Number.isNaN(date.getTime())) return "";
                        return `${formattedDate(date, t)} · ${formattedTime(date)}`;
                      },
                    },
                  }
                : undefined,
              formatter: function (params) {
                if (unitValueChart === "ton") {
                  return `${params[0].data[1].toFixed(2)} ${unitValueChart}`;
                }

                const dataPoint = params[0].data;
                const value = dataPoint[1];
                const dateStr = dataPoint[0];
                const date = new Date(dateStr);

                return `${value}${unitValueChart} - ${formattedDate(date, t)}, ${formattedTime(date)}`;
              },
            },
            toolbox: {
              feature: {
                saveAsImage: {
                  show: showToolbox,
                  title: "Sauvegarder en image",
                  type: "png",
                  lang: ["French"],
                },
              },
            },
            grid: {
              left: "3%",
              right: "4%",
              bottom: "3%",
              containLabel: true,
            },
            xAxis: {
              type: "time",
              boundaryGap: false,
              axisLabel: {
                interval: "auto",
                formatter: function (value) {
                  const date = new Date(value);
                  const hours = date.getHours().toString().padStart(2, "0");
                  const minutes = date.getMinutes().toString().padStart(2, "0");
                  const day = date.getDate();
                  const month = date?.toLocaleString(
                    localeMap[i18n.language] || "en-US",
                    {
                      month: "short",
                    },
                  );
                  if (hours === "00" && minutes === "00") {
                    return `{a|${day} ${month}}`;
                  } else {
                    return `${hours}:${minutes}`;
                  }
                },
                rich: {
                  a: {
                    fontWeight: "bold",
                  },
                },
              },
              splitNumber: 3,
            },
            yAxis: {
              type: "value",
              beginAtZero: true,
              max: maxYAxis,
              min: 0,
              axisLabel: {
                formatter: function (value) {
                  return value + " " + unitValueChart;
                },
              },
              ticks: {
                display: true,
                stepSize: stepYAxis,
              },
            },
            series: generateSeries(),
          };
        };

        const updateChart = () => {
          const newOption = generateOptions();
          chart.setOption(newOption, { notMerge: false });
        };

        updateChart();
        chart.resize();

        chart.off("click");
        chart.on("click", function (params) {
          if (params.seriesType !== "line" || !findHistoryId) return;
          const dataPoint = Array.isArray(params.data) ? params.data : null;
          if (!dataPoint?.[0]) return;

          if (enableHudInteraction) {
            lockedPointRef.current = dataPoint;
            updateChart();
          }
          findHistoryId(dataPoint[0]);
        });

        return () => {
          chart.off("click");
          chartRef.current?.dispose();
          chartRef.current = null;
        };
      }
    },
    // eslint-disable-next-line
    [
      widthWindow,
      parsedSideNavExpandedValue,
      container,
      historyCurrent,
      i18n.language,
      zoomLevel,
      chartHeight,
      enableHudInteraction,
      selectedReadingTime,
      showPackmat,
      showLimitFillingLevel,
    ],
  );

  return (
    <EchartsZoomFrame
      chartId={`${chartName}-chart`}
      chartRef={chartDomRef}
      height={chartHeight}
      width="100%"
    />
  );
};
